<p-dialog
  [(visible)]="visible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="false"
  [closeOnEscape]="true"
  [style]="{ width: '1000px', maxWidth: '96vw' }"
  styleClass="dup-merge-dialog"
  [transitionOptions]="'120ms'"
  (onHide)="onDialogHide()">
  <!-- ===================== HEADER ===================== -->
  <ng-template pTemplate="header">
    <div class="dm-head">
      <span class="dm-bell"><i class="pi pi-bell"></i></span>
      <div class="dm-head__text" *ngIf="state$ | async as state">
        <h5>Duplicate leads found</h5>
        <p>We found {{ state.candidates.length }} leads with the same email. Review the information and select what to keep and merge the rest.</p>
      </div>
    </div>
  </ng-template>

  <!-- ===================== BODY ===================== -->
  <ng-container *ngIf="state$ | async as state">
    <div class="dm-status" *ngIf="state.loading"><i class="pi pi-spin pi-spinner"></i><span>Loading duplicate leads…</span></div>

    <div class="dm-status dm-status--empty" *ngIf="!state.loading && state.candidates.length < 2">
      <i class="pi pi-info-circle"></i>
      <span>{{ state.error || 'No duplicate leads were found for this person.' }}</span>
    </div>

    <div class="dm-body" *ngIf="!state.loading && state.candidates.length >= 2">
      <h6 class="dm-section">Compare leads</h6>

      <!-- --------- compare grid --------- -->
      <!-- top horizontal scrollbar, synced with the grid so overflow leads are
           reachable without scrolling to the bottom of a tall table -->
      <div class="dm-scrolltop" #dmTop (scroll)="dmWrap.scrollLeft = dmTop.scrollLeft">
        <div class="dm-scrolltop__spacer" [style.width.px]="dmWrap.scrollWidth"></div>
      </div>
      <div class="dm-gridwrap" #dmWrap (scroll)="dmTop.scrollLeft = dmWrap.scrollLeft">
        <table class="dm-grid" *ngIf="grid$ | async as grid">
          <thead>
            <tr>
              <th class="dm-grid__corner"></th>
              <th
                *ngFor="let lead of state.candidates; trackBy: trackByLeadId"
                class="dm-grid__lead"
                [class.is-keep]="lead._id === state.keepLeadId"
                (click)="chooseKeep(lead._id)">
                <!-- grid areas: pick | avatar | (name / email / status + date) -->
                <div class="dm-lead">
                  <span class="dm-lead__pick">
                    <p-radioButton name="keepLead" [value]="lead._id" [ngModel]="state.keepLeadId" (onClick)="chooseKeep(lead._id)"></p-radioButton>
                  </span>
                  <span class="dm-avatar">{{ initials(lead) }}</span>
                  <span class="dm-lead__name">
                    <span class="dm-lead__nametext">{{ leadLabel(lead._id) }}</span>
                    <em class="dm-lead__rec" *ngIf="isRecommended(lead._id)">Recommended</em>
                  </span>
                  <span class="dm-lead__mail" [class.is-diff]="isDifferentEmail(lead, state.keepLeadId)" [title]="lead.email || ''">{{
                    lead.email || '—'
                  }}</span>
                  <span class="dm-lead__meta">
                    <span class="dm-badge" *ngIf="displayText(lead.lead_status)" [ngClass]="badgeClass(lead)">
                      {{ displayText(lead.lead_status) }}
                    </span>
                    <span class="dm-lead__when">{{ lead.created_at | date: 'd MMM y' }}</span>
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of grid.rows; trackBy: trackByFieldKey">
              <th class="dm-grid__field">
                {{ row.label }}
                <i
                  class="pi pi-lock dm-grid__lock"
                  *ngIf="row.locked"
                  pTooltip="The Lead Is Always Owned By The Original Owner"
                  tooltipPosition="top"></i>
              </th>
              <td
                *ngFor="let lead of state.candidates; trackBy: trackByLeadId"
                class="dm-grid__cell"
                [class.is-keep]="lead._id === state.keepLeadId"
                [class.is-diff]="row.cells[lead._id]?.different && row.cells[lead._id]?.state !== 'blank'"
                [class.is-picked]="lead._id !== state.keepLeadId && row.cells[lead._id]?.state === 'selected'"
                [class.is-clickable]="row.cells[lead._id]?.state === 'available' || row.cells[lead._id]?.state === 'selected'"
                [class.is-alternate]="row.cells[lead._id]?.isAlternate"
                (click)="toggleCell(row, lead._id)">
                <ng-container *ngIf="row.cells[lead._id] as cell">
                  <span class="dm-cell__value" [class.is-blank]="cell.state === 'blank'">{{ cell.state === 'blank' ? '—' : cell.display }}</span>
                  <span class="dm-cell__alt" *ngIf="cell.isAlternate">Alternative Details</span>

                  <span class="dm-cell__actions">
                    <button
                      type="button"
                      class="dm-cell__altbtn"
                      *ngIf="cell.canAlternate"
                      [class.is-on]="cell.isAlternate"
                      pTooltip="Add As Alternative Details"
                      tooltipPosition="top"
                      (click)="$event.stopPropagation(); openAlternateMenu($event, lead._id, alternateMenu)">
                      <i class="pi pi-id-card"></i>
                    </button>
                    <i class="pi pi-exclamation-circle dm-cell__warn" *ngIf="cell.state === 'blank'"></i>
                    <span
                      class="dm-tick"
                      *ngIf="cell.state !== 'blank'"
                      [class.is-on]="cell.state === 'selected'"
                      [class.is-same]="cell.state === 'same'"
                      [class.is-fixed]="cell.state === 'fixed'">
                      <i class="pi pi-check" *ngIf="cell.state === 'selected' || cell.state === 'same'"></i>
                    </span>
                  </span>
                </ng-container>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </ng-container>

  <p-overlayPanel #alternateMenu appendTo="body" styleClass="dm-alt-menu">
    <ng-template pTemplate="content">
      <button type="button" class="dm-alt-menu__item" [disabled]="!keepContactId" (click)="toggleMenuAlternate(alternateMenu)">
        <i class="pi" [ngClass]="isAlternate(menuLeadId) ? 'pi-times' : 'pi-id-card'"></i>
        {{ isAlternate(menuLeadId) ? 'Remove Alternative Details' : 'Add As Alternative Details' }}
      </button>
      <small class="dm-alt-menu__hint" *ngIf="keepContactId; else noContact">
        Saves the name, email and mobile of {{ leadLabel(menuLeadId) }} on the contact without changing its own details.
      </small>
      <ng-template #noContact>
        <small class="dm-alt-menu__hint">The lead you're keeping isn't linked to a contact yet.</small>
      </ng-template>
    </ng-template>
  </p-overlayPanel>

  <!-- ===================== FOOTER ===================== -->
  <ng-template pTemplate="footer">
    <ng-container *ngIf="state$ | async as state">
      <div class="dm-actions" *ngIf="state.candidates.length >= 2">
        <div class="dm-card dm-card--main">
          <span class="dm-card__icon"><i class="pi pi-check-circle"></i></span>
          <div class="dm-card__body">
            <strong>Set As Main Lead</strong>
            <small>This lead will be used as the primary record.</small>
            <ng-select
              class="dm-main-select"
              [items]="leadOptions"
              bindValue="value"
              bindLabel="label"
              [ngModel]="state.keepLeadId"
              [clearable]="false"
              [searchable]="leadOptions.length > 8"
              appendTo="body"
              (change)="chooseKeep($event.value)">
              <ng-template ng-label-tmp let-item="item">
                <span class="dm-opt">
                  <span class="dm-opt__name">{{ item?.label }}</span>
                  <span class="dm-opt__rec" *ngIf="item?.recommended">(Recommended)</span>
                </span>
              </ng-template>
              <ng-template ng-option-tmp let-item="item">
                <span class="dm-opt">
                  <span class="dm-opt__name">{{ item?.label }}</span>
                  <span class="dm-opt__meta">{{ item?.createdAt | date: 'dd MMM, y' }}</span>
                  <span class="dm-opt__rec" *ngIf="item?.recommended">(Recommended)</span>
                </span>
              </ng-template>
            </ng-select>
          </div>
        </div>

        <div class="dm-card dm-card--merge" *ngIf="grid$ | async as grid">
          <span class="dm-card__icon"><i class="pi pi-sitemap"></i></span>
          <div class="dm-card__body">
            <strong>
              Merge Selected Information
              <i
                class="pi pi-info-circle dm-card__info"
                pTooltip="Tick A Field On Any Lead To Use Its Value. Notes And Tags Can Be Ticked On More Than One Lead To Keep Them All."
                tooltipPosition="top"></i>
            </strong>
            <small>The selected fields are merged into {{ keepLeadLabel }}. The other leads are closed as duplicates.</small>
            <div class="dm-summary">
              <ng-container *ngFor="let lead of state.candidates; let last = last; trackBy: trackByLeadId">
                <span class="dm-summary__pill" [class.is-keep]="lead._id === state.keepLeadId">
                  <strong>{{ leadLabel(lead._id) }}</strong> · {{ grid.counts[lead._id] || 0 }}
                  {{ grid.counts[lead._id] === 1 ? 'Field Selected' : 'Fields Selected' }}
                </span>
                <span class="dm-summary__plus" *ngIf="!last">+</span>
              </ng-container>
            </div>
            <div class="dm-summary dm-summary--alt" *ngIf="(alternateLeadIds$ | async)?.length">
              <button
                type="button"
                class="dm-summary__alt"
                *ngFor="let leadId of alternateLeadIds$ | async; trackBy: trackByLeadIdValue"
                pTooltip="Remove Alternative Details"
                tooltipPosition="top"
                (click)="removeAlternate(leadId)">
                <i class="pi pi-id-card"></i>
                Alternative Details ({{ leadLabel(leadId) }})
                <i class="pi pi-times"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="dm-foot">
        <button type="button" class="dm-btn dm-btn--ghost" (click)="cancel()" [disabled]="state.saving">Cancel</button>
        <span class="dm-foot__notice" *ngIf="state.candidates.length >= 2 && !canCloseDuplicates">
          <i class="pi pi-lock"></i>
          Only {{ originalOwnerName }}, the owner of the original lead, can merge these leads.
        </span>
        <button
          type="button"
          class="dm-btn dm-btn--primary"
          *ngIf="state.candidates.length >= 2"
          (click)="confirmOrClose()"
          [disabled]="state.saving || !state.keepLeadId || !canCloseDuplicates">
          <i class="pi pi-spin pi-spinner" *ngIf="state.saving"></i>
          Merge Leads
        </button>
      </div>
    </ng-container>
  </ng-template>
</p-dialog>
